export class BuiltInActionSet {
    private actionMapping: Map<
        DefinedBuiltIns,
        (arg: any[]) => void
    > = new Map();

    /**
     * This method is called when the interpreter starts execution. It is called from the method {@link XLogoInterpreter#exec}
     */
    public executionStarted(): void {}

    /**
     * This method is called when the interpreter has finished execution. Override this function to implement it.
     */
    public executionFinished(): void {}

    /**
     * This function is called when no action is registered for the given built-in command. To change the behavior override this function.
     * @param arg Argument for the action
     */
    public defaultAction(arg: any[]) {}

    /**
     * This action is called when the command 'print' is called in an XLogo program. The default is to write to the console output. To change the behaviour override this function.
     * @param arg Argument for print. Can be string or any valid XLogo runtime value
     */
    public printAction(arg: any): void {
        console.log(arg);
    }

    /**
     * Register a function for a certain built-in command. The function is called when the corresponding built-in command is called in an XLogo program.
     * Only one function can be registered for a built-in command per action set.
     * @param command The enum entry of the command to override
     * @param action A function taking an array of type any.
     */
    public registerCommandAction(
        command: DefinedBuiltIns,
        action: (arg: any[]) => void,
    ): void {
        this.actionMapping.set(command, action);
    }

    public getActionForCommand(command: DefinedBuiltIns): (arg: any[]) => void {
        let result = this.actionMapping.get(command);
        if (!result) result = this.defaultAction;

        return result;
    }
}

export enum DefinedBuiltIns {
    FORWARD,
    BACKWARD,
    LEFT,
    RIGHT,
    SETPENCOLOR,
    CLEARSCREEN,
    PENERASE,
    PENPAINT,
    PENDOWN,
    PENUP,
    HOME,
    SHOWTURTLE,
    HIDETURTLE,
    CLEARHISTORY,
    SETPENWIDTH,
    WASH,
    SETSCREENCOLOR,
    SETXY,
    SETX,
    SETY,
    CIRCLE,
    SETHEADING,
    GENERATESOLUTIONS,
}

export enum DefinedSpecialBuiltIns {
    WAIT,
    STOP,
    STOPALL,
}

export interface BuiltInCommandStructure {
    command: DefinedBuiltIns;
    names: string[];
    // TODO: Make args a list of whatever a type will be to check at compile
    // time if argument would match
    args: number;
}

export interface SpecialBuiltInCommandStructure {
    command: DefinedSpecialBuiltIns;
    names: string[];
    // TODO: Make args a list of whatever a type will be to check at compile
    // time if argument would match
    args: number;
}

//TODO: Change args that they reflect the types required
//TODO: Maybe make names visible to test if a misspelled input could be one of those?
const builtIns: BuiltInCommandStructure[] = [
    {
        command: DefinedBuiltIns.FORWARD,
        names: ["forward", "fd"],
        args: 1,
    },
    {
        command: DefinedBuiltIns.BACKWARD,
        names: ["backward", "back", "bk"],
        args: 1,
    },
    {
        command: DefinedBuiltIns.LEFT,
        names: ["left", "lt"],
        args: 1,
    },
    {
        command: DefinedBuiltIns.RIGHT,
        names: ["right", "rt"],
        args: 1,
    },
    {
        command: DefinedBuiltIns.SETPENCOLOR,
        names: ["setpencolor", "setpc"],
        args: 1,
    },
    {
        command: DefinedBuiltIns.CLEARSCREEN,
        names: ["clearscreen", "cs"],
        args: 0,
    },
    {
        command: DefinedBuiltIns.PENERASE,
        names: ["penerase", "pe"],
        args: 0,
    },
    {
        command: DefinedBuiltIns.PENPAINT,
        names: ["penpaint", "ppt"],
        args: 0,
    },
    {
        command: DefinedBuiltIns.PENDOWN,
        names: ["pendown", "pd"],
        args: 0,
    },
    {
        command: DefinedBuiltIns.PENUP,
        names: ["penup", "pu"],
        args: 0,
    },
    {
        command: DefinedBuiltIns.HOME,
        names: ["home"],
        args: 0,
    },
    {
        command: DefinedBuiltIns.SHOWTURTLE,
        names: ["showturtle", "st"],
        args: 0,
    },
    {
        command: DefinedBuiltIns.HIDETURTLE,
        names: ["hideturtle", "ht"],
        args: 0,
    },
    {
        command: DefinedBuiltIns.CLEARHISTORY,
        names: ["ct"],
        args: 0,
    },
    {
        command: DefinedBuiltIns.SETPENWIDTH,
        names: ["setpenwidth", "setpw"],
        args: 1,
    },
    {
        command: DefinedBuiltIns.WASH,
        names: ["wash"],
        args: 0,
    },
    {
        command: DefinedBuiltIns.SETSCREENCOLOR,
        names: ["setscreencolor", "setsc"],
        args: 1,
    },
    {
        command: DefinedBuiltIns.SETXY,
        names: ["setxy"],
        args: 2,
    },
    {
        command: DefinedBuiltIns.SETX,
        names: ["setx"],
        args: 1,
    },
    {
        command: DefinedBuiltIns.SETY,
        names: ["sety"],
        args: 1,
    },
    {
        command: DefinedBuiltIns.CIRCLE,
        names: ["circle"],
        args: 1,
    },
    {
        command: DefinedBuiltIns.SETHEADING,
        names: ["setheading"],
        args: 1,
    },
    {
        command: DefinedBuiltIns.GENERATESOLUTIONS,
        names: ["generatesolutions"],
        args: 0,
    },
];

const specialBuiltIn: SpecialBuiltInCommandStructure[] = [
    {
        command: DefinedSpecialBuiltIns.STOP,
        names: ["stop"],
        args: 0,
    },
    {
        command: DefinedSpecialBuiltIns.STOPALL,
        names: ["stopall"],
        args: 0,
    },
    {
        command: DefinedSpecialBuiltIns.WAIT,
        names: ["wait"],
        args: 1,
    },
];

// Create a mapping for all command names. This simplifies the lookup process
// as we don't have to traverse the list of all command.
const allBuiltInNames: Map<string, BuiltInCommandStructure> = new Map<
    string,
    BuiltInCommandStructure
>();
builtIns.forEach(item => {
    item["names"].forEach(alias => {
        allBuiltInNames.set(alias, item);
    });
});

const allSpecialBuiltInNames: Map<
    string,
    SpecialBuiltInCommandStructure
> = new Map<string, SpecialBuiltInCommandStructure>();
specialBuiltIn.forEach(item => {
    item["names"].forEach(alias => {
        allSpecialBuiltInNames.set(alias, item);
    });
});

/**
 * getBuiltInCommandStructure
 * Gets the structure for a built in command.
 * @param name Name of the command to get the structure for
 * @returns If the command exists it returns the structure, undefined otherwise
 */
export function getBuiltInCommandStructure(
    name: string,
): BuiltInCommandStructure | undefined {
    return allBuiltInNames.get(name);
}

export function getSpecialBuiltInCommandStructure(
    name: string,
): SpecialBuiltInCommandStructure | undefined {
    return allSpecialBuiltInNames.get(name);
}



// WEBPACK FOOTER //
// ./src/app/xlogo-parser/ir/builtInCommands.ts